/**
 * admin/makua/AdminAccommodation.js — Operations → Accommodation (V2.1, Page 6).
 *
 * The permanent inventory: room types (what guests choose) and the real
 * rooms and dorm beds underneath (what the system assigns). Everything
 * about dates (who is where, maintenance, holds) is the Booking Calendar's.
 *
 *   - Each room type has a gallery of the whole place; each room and each
 *     bed has its own, separate gallery.
 *   - Room types, rooms and beds keep the order staff give them (drag, or
 *     the arrow buttons); that order is used everywhere, the calendar too.
 *   - Deleting something that has stays on record archives it instead.
 *
 * The add-on library moved to Operations → Activities & Add-ons.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import AdminLayout from "../AdminLayout";
import { usePermissions, hasPermission } from "../permissions";
import { inventoryAPI, roomTypesAPI, roomsAPI, mediaUrl } from "../../services/api";
import { useLoad, Field, Modal, money, Badge } from "./ops";
import { useStored, useReorder, ReorderHandle, useUnsaved, changed, Gallery } from "./kit";
import "./ops.css";

export default function AdminAccommodation() {
  return (
    <AdminLayout pageTitle="Accommodation" pageSubtitle="Room types, every real room and bed, and their photos. Dates and occupancy are in the Booking Calendar."
      permission="inventory.view" feature="reservations">
      <Inventory />
    </AdminLayout>
  );
}

const unitCount = (c) => (c.sell_by === "bed"
  ? c.rooms.filter((r) => r.active).reduce((n, r) => n + r.beds.filter((b) => b.active).length, 0)
  : c.rooms.filter((r) => r.active).length);
const isArchived = (x) => !!x.archived_at;

function Inventory() {
  const { admin } = usePermissions();
  const canEdit = hasPermission(admin, "blocks.edit");
  const { data, error, reload } = useLoad(() => inventoryAPI.tree(), []);
  const [q, setQ] = useStored("acc:q", "");
  const [sortMode, setSortMode] = useStored("acc:sort", "custom");
  const [showArchived, setShowArchived] = useStored("acc:archived", false);
  const [folded, setFolded] = useStored("acc:folded", {});
  const [editType, setEditType] = useState(null);
  const [editRoom, setEditRoom] = useState(null);
  const cats = useMemo(() => data?.categories || [], [data]);

  const needle = q.trim().toLowerCase();
  const matchRoom = (r) => !needle || [r.name, r.code, r.area, ...r.beds.map((b) => b.name)].some((v) => String(v || "").toLowerCase().includes(needle));
  const view = cats
    .filter((c) => showArchived || !isArchived(c))
    .map((c) => {
      const catHit = !needle || c.name.toLowerCase().includes(needle) || String(c.group_name || "").toLowerCase().includes(needle);
      let rooms = c.rooms.filter((r) => (showArchived || !isArchived(r)) && (catHit || matchRoom(r)));
      if (sortMode === "az") rooms = [...rooms].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
      return { ...c, rooms, hit: catHit || rooms.length > 0 };
    })
    .filter((c) => c.hit);
  const ordered = sortMode === "az" ? [...view].sort((a, b) => a.name.localeCompare(b.name)) : view;
  const canDrag = canEdit && sortMode === "custom" && !needle;
  const typeOrder = useReorder(ordered, async (ids) => { await roomTypesAPI.reorder(ids); toast.success("Order saved."); });

  return (
    <div className="ops">
      <div className="ops-bar ops-toolbar">
        <Field label="Search" className="ops-search">
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Room type, room, bed or unit ID" />
        </Field>
        <div className="ops-field"><span>Order</span>
          <div className="ops-seg" role="group" aria-label="Order">
            <button type="button" aria-pressed={sortMode === "custom"} onClick={() => setSortMode("custom")}>Custom</button>
            <button type="button" aria-pressed={sortMode === "az"} onClick={() => setSortMode("az")}>A–Z</button>
          </div>
        </div>
        <label className="ops-check"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />Show archived</label>
        <span className="ops-spacer" />
        {canEdit && <button className="ops-btn ops-btn--primary" onClick={() => setEditType({})}>New room type</button>}
      </div>
      <p className="ops-muted" style={{ margin: 0 }}>
        Guests choose a room type; the system gives them a real room or bed for every night. A type with no rooms in it can't be sold.
        {canDrag ? " Drag room types, rooms and beds (or use the arrows) to set the order used everywhere, the calendar included." : sortMode === "az" ? " Showing A–Z; switch to Custom to change the order." : ""}
        {" "}Add-ons are now under <Link to="/admin/activities">Activities &amp; Add-ons</Link>.
      </p>
      {error && <p className="ops-error">{error}</p>}
      {!data && !error && <p className="ops-muted">Loading…</p>}
      {data && !ordered.length && <p className="ops-note">{needle ? `Nothing matches "${q}".` : "No room types yet."}</p>}

      {(canDrag ? typeOrder.items : ordered).map((c) => {
        const open = !!needle || !folded[c.id];
        const n = unitCount(c);
        return (
          <section key={c.id} className="ops-card ops-acc-cat" style={c.active ? null : { opacity: 0.7 }} aria-label={c.name} {...(canDrag ? typeOrder.rowProps(c.id) : {})}>
            <div className="ops-card-head">
              <div style={{ display: "flex", gap: "0.6rem", alignItems: "center", minWidth: 0 }}>
                <button type="button" className="ops-icon-btn" aria-expanded={open} aria-label={`${open ? "Fold" : "Unfold"} ${c.name}`}
                  onClick={() => setFolded({ ...folded, [c.id]: open })}>{open ? "▾" : "▸"}</button>
                {canDrag && <ReorderHandle r={typeOrder} id={c.id} label={c.name} />}
                {c.photos[0] && <img src={mediaUrl(c.photos[0].file_path)} alt="" width="56" height="40" style={{ objectFit: "cover", borderRadius: 6 }} />}
                <div style={{ minWidth: 0 }}>
                  <h3 style={{ margin: 0 }}>
                    {c.name} {c.is_base ? <Badge tone="green">Included base</Badge> : null} {c.request_only ? <Badge tone="purple">By request</Badge> : null}
                    {isArchived(c) ? <Badge tone="red">Archived</Badge> : !c.active ? <Badge>Not offered</Badge> : null}
                  </h3>
                  <span className="ops-sub">
                    {c.group_name ? `${c.group_name} · ` : ""}{c.sell_by === "bed" ? "Sold by the bed" : `Sold by the room, up to ${c.max_guests} guests`}
                    {" · "}{c.rooms.filter((r) => r.active).length} rooms{c.sell_by === "bed" ? ` · ${n} beds` : ""}
                    {" · "}{c.is_base ? "included" : c.request_only ? "price on request" : `+${money(c.nightly_rate)} a ${c.sell_by === "bed" ? "bed" : "room"} a night`}
                    {" · "}{c.meal_service} meals · {c.photos.length} group photo{c.photos.length === 1 ? "" : "s"}
                  </span>
                </div>
              </div>
              <button className="ops-btn ops-btn--sm" onClick={() => setEditType(c)}>{canEdit ? "Edit type" : "View type"}</button>
            </div>
            {open && <RoomList c={c} canEdit={canEdit} canDrag={canDrag} onEdit={setEditRoom} onChanged={reload} />}
          </section>
        );
      })}
      {editType && <CategoryModal c={editType} canEdit={canEdit} onClose={() => setEditType(null)} onSaved={(keepOpen) => { reload(); if (!keepOpen) setEditType(null); }} />}
      {editRoom && (() => {
        // Always the latest copy of the room, so beds and photos added in the editor show at once.
        const cat = cats.find((x) => x.rooms.some((r) => r.id === editRoom));
        const room = cat?.rooms.find((r) => r.id === editRoom);
        return room ? <RoomModal key={room.id} room={room} cat={cat} cats={cats} canEdit={canEdit} onClose={() => { setEditRoom(null); reload(); }} onSaved={reload} /> : null;
      })()}
    </div>
  );
}

function RoomList({ c, canEdit, canDrag, onEdit, onChanged }) {
  const [adding, setAdding] = useState(false);
  const blank = { name: "", code: "", area: "", bed_count: c.sell_by === "bed" ? 6 : 0 };
  const [f, setF] = useState(blank);
  const order = useReorder(c.rooms, async (ids) => { await roomsAPI.reorder(c.id, ids); toast.success("Order saved."); onChanged(); });
  const rooms = canDrag ? order.items : c.rooms;
  const run = async (fn, ok) => { try { const r = await fn(); toast.success(r?.message || ok); onChanged(); return r; } catch (err) { toast.error(err.message); return null; } };
  const add = async () => {
    const r = await run(() => roomsAPI.create({ room_type_id: c.id, ...f }), "Room added.");
    if (r) { setAdding(false); setF(blank); }
  };
  return (
    <>
      <div className="ops-table-wrap" style={{ border: 0 }} tabIndex={0} role="region" aria-label={`Rooms in ${c.name}`}>
        <table className="ops-table">
          <thead>
            <tr>
              {canDrag && <th><span className="ops-visually-hidden">Order</span></th>}
              <th>Unit ID</th><th>Room</th><th>Area</th><th>{c.sell_by === "bed" ? "Beds" : "Sleeps"}</th><th>Photos</th><th />
            </tr>
          </thead>
          <tbody>
            {!rooms.length && <tr><td colSpan={7} className="ops-empty">No rooms yet.{canEdit ? " Add the first one below." : ""}</td></tr>}
            {rooms.map((r) => (
              <tr key={r.id} style={r.active ? null : { opacity: 0.55 }} {...(canDrag ? order.rowProps(r.id) : {})}>
                {canDrag && <td><ReorderHandle r={order} id={r.id} label={r.name} /></td>}
                <td>{r.code || <span className="ops-muted">—</span>}</td>
                <td>
                  <button className="ops-row-btn" onClick={() => onEdit(r.id)}>{r.name}</button>
                  {isArchived(r) ? <span className="ops-sub">archived: has stays on record</span> : !r.active ? <span className="ops-sub">switched off</span> : null}
                </td>
                <td>{r.area || "—"}</td>
                <td>{c.sell_by === "bed"
                  ? <>{r.beds.filter((b) => b.active).length}<span className="ops-sub">{r.beds.filter((b) => b.active).map((b) => b.name).join(", ")}</span></>
                  : r.max_guests || c.max_guests}{c.sell_by !== "bed" && !r.max_guests && <span className="ops-sub">type default</span>}</td>
                <td>{r.gallery.count ? `${r.gallery.count} photo${r.gallery.count === 1 ? "" : "s"}` : <span className="ops-muted">None</span>}
                  {c.sell_by === "bed" && r.beds.some((b) => b.gallery.count) && <span className="ops-sub">+ {r.beds.reduce((n, b) => n + b.gallery.count, 0)} on beds</span>}</td>
                <td className="num">
                  <button className="ops-btn ops-btn--sm" onClick={() => onEdit(r.id)}>{canEdit ? "Edit room" : "View"}</button>
                  {canEdit && <>{" "}<button className="ops-btn ops-btn--sm" onClick={() => run(() => roomsAPI.duplicate(r.id), "Copied.")}>Duplicate</button></>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canEdit && (adding ? (
        <div className="ops-grid ops-grid--4" style={{ marginTop: "0.75rem", alignItems: "end" }}>
          <Field label="Room name"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Nirvana 6" /></Field>
          <Field label="Unit ID" hint="Optional: a code staff use, e.g. N6"><input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} /></Field>
          <Field label="Area or building"><input value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} /></Field>
          {c.sell_by === "bed" && <Field label="Beds"><input type="number" min="1" max="20" value={f.bed_count} onChange={(e) => setF({ ...f, bed_count: e.target.value })} /></Field>}
          <div className="ops-actions" style={{ justifyContent: "flex-start" }}>
            <button className="ops-btn" onClick={() => setAdding(false)}>Cancel</button>
            <button className="ops-btn ops-btn--primary" disabled={!f.name.trim()} onClick={add}>Add room</button>
          </div>
        </div>
      ) : <button className="ops-btn ops-btn--sm" style={{ marginTop: "0.6rem" }} onClick={() => setAdding(true)}>Add a room</button>)}
    </>
  );
}

/* ══════════════════════ ROOM / BED EDITOR ══════════════════════ */
const roomForm = (r) => ({
  name: r.name || "", code: r.code || "", area: r.area || "", room_type_id: r.room_type_id, max_guests: r.max_guests ?? "",
  bathroom: r.bathroom || "", amenities: r.amenities || "", description: r.description || "", notes: r.notes || "", active: !!r.active,
});

function RoomModal({ room, cat, cats, canEdit, onClose, onSaved }) {
  const [base, setBase] = useState(() => roomForm(room));
  const [f, setF] = useState(base);
  const [bed, setBed] = useState(null);
  const dirty = changed(f, base);
  const guard = useUnsaved(dirty);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async () => {
    try {
      const r = await roomsAPI.update(room.id, { ...f, max_guests: f.max_guests === "" ? null : f.max_guests });
      toast.success(r.message || "Saved."); setBase(roomForm(r.data)); setF(roomForm(r.data)); onSaved();
    } catch (err) { toast.error(err.message); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${room.name}? A room with stays on record is archived instead, and its history kept.`)) return;
    try { const r = await roomsAPI.remove(room.id); toast.success(r.message); onClose(); } catch (err) { toast.error(err.message); }
  };
  const beds = useReorder(room.beds, async (ids) => { await roomsAPI.reorderBeds(room.id, ids); toast.success("Order saved."); onSaved(); });
  const run = async (fn, ok) => { try { const r = await fn(); toast.success(r?.message || ok); onSaved(); } catch (err) { toast.error(err.message); } };
  const bedCat = cat.sell_by === "bed";
  return (
    <Modal title={canEdit ? `Edit ${room.name}` : room.name} onClose={guard(onClose)} wide>
      <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0 }}>
        <div className="ops-grid ops-grid--3">
          <Field label="Room name"><input value={f.name} onChange={set("name")} /></Field>
          <Field label="Unit ID" hint="The code staff use"><input value={f.code} onChange={set("code")} /></Field>
          <Field label="Area or building"><input value={f.area} onChange={set("area")} /></Field>
          <Field label="Room type" hint="Moving it keeps its stays">
            <select value={f.room_type_id} onChange={set("room_type_id")}>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
          </Field>
          {!bedCat && <Field label="Sleeps" hint={`Empty uses the type's ${cat.max_guests}`}><input type="number" min="1" max="30" value={f.max_guests} onChange={set("max_guests")} /></Field>}
          <Field label="Bathroom" hint={cat.bathroom ? `Type: ${cat.bathroom}` : undefined}><input value={f.bathroom} onChange={set("bathroom")} placeholder="Private, ensuite" /></Field>
          <Field label="Amenities" hint={cat.amenities ? `Type: ${cat.amenities}` : "Comma separated"} className="ops-span-2"><input value={f.amenities} onChange={set("amenities")} placeholder="AC, Balcony" /></Field>
          <Field label="Description" hint="What's particular to this room" className="ops-span-all"><textarea value={f.description} onChange={set("description")} rows={3} /></Field>
          <Field label="Staff notes" hint="Not shown to guests" className="ops-span-all"><textarea value={f.notes} onChange={set("notes")} rows={2} /></Field>
        </div>
        <label className="ops-check"><input type="checkbox" checked={f.active} onChange={set("active")} />In use: offered for new stays{room.archived_at ? " (switching it on restores it from the archive)" : ""}</label>
      </fieldset>
      {canEdit && (
        <div className="ops-actions">
          <button className="ops-btn ops-btn--danger" style={{ marginRight: "auto" }} onClick={remove}>Delete room</button>
          {dirty && <span className="ops-dirty">Unsaved changes</span>}
          <button className="ops-btn" onClick={guard(onClose)}>Close</button>
          <button className="ops-btn ops-btn--primary" disabled={!dirty || !f.name.trim()} onClick={save}>Save changes</button>
        </div>
      )}

      <Gallery kind="room" ownerId={room.id} title={`Photos of ${room.name}`} hint={`Only this room. The ${cat.name} photos are separate.`} canEdit={canEdit} onChange={onSaved} />

      {bedCat && (
        <div className="ops-card">
          <div className="ops-card-head"><h3>Beds in {room.name}</h3>
            {canEdit && <button className="ops-btn ops-btn--sm" onClick={() => run(() => roomsAPI.addBed(room.id), "Bed added.")}>Add bed</button>}</div>
          <table className="ops-table">
            <thead><tr>{canEdit && <th />}<th>Bed</th><th>Description</th><th>Photos</th><th /></tr></thead>
            <tbody>{beds.items.map((b) => (
              <tr key={b.id} style={b.active ? null : { opacity: 0.55 }} {...(canEdit ? beds.rowProps(b.id) : {})}>
                {canEdit && <td><ReorderHandle r={beds} id={b.id} label={b.name} /></td>}
                <td><b>{b.name}</b>{!b.active && <span className="ops-sub">switched off</span>}</td>
                <td>{b.description || "—"}</td>
                <td>{b.gallery?.count || 0}</td>
                <td className="num"><button className="ops-btn ops-btn--sm" onClick={() => setBed(b)}>{canEdit ? "Edit bed" : "View"}</button></td>
              </tr>))}
            </tbody>
          </table>
        </div>
      )}
      {bed && <BedModal room={room} bed={bed} canEdit={canEdit} onClose={() => { setBed(null); onSaved(); }} />}
    </Modal>
  );
}

function BedModal({ room, bed, canEdit, onClose }) {
  const [base, setBase] = useState({ name: bed.name, description: bed.description || "", active: !!bed.active });
  const [f, setF] = useState(base);
  const dirty = changed(f, base);
  const guard = useUnsaved(dirty);
  const save = async () => {
    try { const r = await roomsAPI.updateBed(room.id, bed.id, f); toast.success(r.message); setBase(f); } catch (err) { toast.error(err.message); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${bed.name} from ${room.name}? A bed with stays on record is switched off instead.`)) return;
    try { const r = await roomsAPI.removeBed(room.id, bed.id); toast.success(r.message); onClose(); } catch (err) { toast.error(err.message); }
  };
  return (
    <Modal title={`${room.name} · ${bed.name}`} onClose={guard(onClose)}>
      <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0 }}>
        <div className="ops-grid ops-grid--2">
          <Field label="Bed name"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <label className="ops-check" style={{ alignSelf: "end" }}><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />In use</label>
          <Field label="Description" hint="Lower bunk, by the window…" className="ops-span-all"><input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
        </div>
      </fieldset>
      {canEdit && (
        <div className="ops-actions">
          <button className="ops-btn ops-btn--danger" style={{ marginRight: "auto" }} onClick={remove}>Remove bed</button>
          {dirty && <span className="ops-dirty">Unsaved changes</span>}
          <button className="ops-btn" onClick={guard(onClose)}>Close</button>
          <button className="ops-btn ops-btn--primary" disabled={!dirty || !f.name.trim()} onClick={save}>Save changes</button>
        </div>
      )}
      <Gallery kind="bed" ownerId={bed.id} title={`Photos of ${bed.name}`} hint="Only this bed." canEdit={canEdit} />
    </Modal>
  );
}

/* ══════════════════════ ROOM TYPE (GROUP) EDITOR ══════════════════════ */
const catForm = (c) => ({
  name: c.name || "", group_name: c.group_name || "", summary: c.summary || "", description: c.description || "",
  sell_by: c.sell_by || "room", max_guests: c.max_guests || 2, is_base: !!c.is_base, request_only: !!c.request_only,
  meal_service: c.meal_service || "plated", nightly_rate: Number(c.nightly_rate || 0), bathroom: c.bathroom || "",
  amenities: c.amenities || "", active: c.id ? !!c.active : true,
});

function CategoryModal({ c, canEdit, onClose, onSaved }) {
  const [id, setId] = useState(c.id || null);
  const [base, setBase] = useState(() => catForm(c));
  const [f, setF] = useState(base);
  const dirty = changed(f, base);
  const guard = useUnsaved(dirty);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async () => {
    try {
      const payload = { ...f, nightly_rate: f.is_base ? 0 : f.nightly_rate, is_free: f.is_base };
      const res = id ? await roomTypesAPI.update(id, payload) : await roomTypesAPI.create(payload);
      setBase(f);
      if (!id) { setId(res.data.id); toast.success("Room type added. Now add its photos and rooms."); onSaved(true); return; }
      toast.success("Saved."); onSaved(true);
    } catch (err) { toast.error(err.message); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${f.name}? A room type with reservations or stays is archived instead, and its rooms and history kept.`)) return;
    try { const r = await roomTypesAPI.remove(id); toast.success(r.message); onSaved(false); } catch (err) { toast.error(err.message); }
  };
  return (
    <Modal title={id ? (canEdit ? `Edit ${base.name || "room type"}` : base.name) : "New room type"} onClose={guard(onClose)} wide>
      <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0 }}>
        <div className="ops-grid ops-grid--3">
          <Field label="Name"><input value={f.name} onChange={set("name")} /></Field>
          <Field label="Group on the website" hint="e.g. Shared & budget"><input value={f.group_name} onChange={set("group_name")} /></Field>
          <Field label="Sold as">
            <select value={f.sell_by} onChange={set("sell_by")}><option value="bed">One bed per guest (dorm, hostel)</option><option value="room">The whole room</option></select>
          </Field>
          {f.sell_by === "room" && <Field label="Most guests per room" hint="Each room can set its own"><input type="number" min="1" max="30" value={f.max_guests} onChange={set("max_guests")} /></Field>}
          <Field label="Meals">
            <select value={f.meal_service} onChange={set("meal_service")}><option value="buffet">Buffet</option><option value="plated">Plated</option></select>
          </Field>
          <Field label={`Upgrade per ${f.sell_by === "bed" ? "bed" : "room"} per night (USD)`} hint="On top of the retreat price. Shared guests pay the room's upgrade once.">
            <input type="number" min="0" value={f.nightly_rate} onChange={set("nightly_rate")} disabled={f.is_base || f.request_only} />
          </Field>
          <Field label="Bathroom" hint="Default for its rooms"><input value={f.bathroom} onChange={set("bathroom")} placeholder="Private, Shared" /></Field>
          <Field label="Amenities" hint="Comma separated; default for its rooms" className="ops-span-2"><input value={f.amenities} onChange={set("amenities")} placeholder="AC, Balcony, Hot tub" /></Field>
          <Field label="Short line" className="ops-span-all"><input value={f.summary} onChange={set("summary")} /></Field>
          <Field label="Description" className="ops-span-all"><textarea value={f.description} onChange={set("description")} /></Field>
        </div>
        <div className="ops-grid ops-grid--3">
          <label className="ops-check"><input type="checkbox" checked={f.is_base} onChange={set("is_base")} />Included in the retreat price (only one type can be)</label>
          <label className="ops-check"><input type="checkbox" checked={f.request_only} onChange={set("request_only")} />By request only: staff approve and price it</label>
          <label className="ops-check"><input type="checkbox" checked={f.active} onChange={set("active")} />Offered to guests{c.archived_at ? " (restores it from the archive)" : ""}</label>
        </div>
      </fieldset>
      {canEdit && (
        <div className="ops-actions">
          {id && <button className="ops-btn ops-btn--danger" onClick={remove} style={{ marginRight: "auto" }}>Delete room type</button>}
          {dirty && <span className="ops-dirty">Unsaved changes</span>}
          <button className="ops-btn" onClick={guard(onClose)}>Close</button>
          <button className="ops-btn ops-btn--primary" disabled={!f.name.trim() || (id && !dirty)} onClick={save}>{id ? "Save changes" : "Add room type"}</button>
        </div>
      )}
      {id ? (
        <Gallery kind="room_type" ownerId={id} title={`Photos of ${base.name || "this room type"}`} hint="The whole place, shared by its rooms. Each room's own photos are in Edit room." canEdit={canEdit} onChange={() => onSaved(true)} />
      ) : <p className="ops-note">Add the room type first; then its photos.</p>}
    </Modal>
  );
}
